import React from 'react';
import { FormControlLabel, Tooltip } from '@material-ui/core';
import { OndemandVideo, HelpOutline } from '@material-ui/icons';
import { TextField, PrimaryCheckbox } from '../../components/AtomComponents';
import { QuickBooksData, QBO_TUTORIAL_VIDEO_URL } from './types';
import { StepCard, StepField, StepHeader, StepFormContainer } from './PxwStepComponents';

interface QuickBooksStepProps {
  data: QuickBooksData;
  onChange: (data: QuickBooksData) => void;
  enabled: boolean;
  onToggleEnabled: (enabled: boolean) => void;
}

const QuickBooksStep = ({ data, onChange, enabled, onToggleEnabled }: QuickBooksStepProps) => {
  const set = (field: keyof QuickBooksData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange({ ...data, [field]: e.target.value.trim() });
  const setCheckbox = (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => onChange({ ...data, manuallyConfigureSalesTax: checked });

  const handleVideoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(QBO_TUTORIAL_VIDEO_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <StepCard>
      <StepHeader
        title="QuickBooks (QBO)"
        subtitle={<>Credentials, PO charges &amp; sales tax &mdash; configured via&nbsp;<code className="pxw-code-chip">PxwTenantErpConfigQboConfigurator</code>&nbsp;+&nbsp;<code className="pxw-code-chip">PxwTenantQboSetup</code></>}
      />
      <StepFormContainer>
        <div className="small-12 margin-bottom-3">
          <p className="pxw-field-label margin-bottom-1">Enable Integration</p>
          <FormControlLabel label="Enable QuickBooks Integration" control={<PrimaryCheckbox name="enableQuickBooksIntegration" checked={enabled} onChange={(_e, checked) => onToggleEnabled(checked)} />} />
        </div>

        {enabled && (
          <>
            <StepField label="Realm ID" wide>
              <TextField size="small" fullWidth required name="realmId" placeholder="qbo-realm-id" value={data.realmId} onChange={set('realmId')} />
            </StepField>

            <StepField label="QBO Client ID" wide>
              <TextField size="small" fullWidth required name="qboClientId" placeholder="qbo-client-id" value={data.qboClientId} onChange={set('qboClientId')} />
            </StepField>

            <StepField label="QBO Client Secret" wide>
              <TextField size="small" fullWidth required name="qboClientSecret" placeholder="qbo-client-secret" value={data.qboClientSecret} onChange={set('qboClientSecret')} />
            </StepField>

            <StepField label="Refresh Token" wide>
              <TextField size="small" fullWidth required name="refreshToken" placeholder="qbo-refresh-token" value={data.refreshToken} onChange={set('refreshToken')} />
            </StepField>

            <StepField label="Access Token" wide>
              <TextField size="small" fullWidth required multiline rows={3} name="accessToken" placeholder="qbo-access-token" value={data.accessToken} onChange={set('accessToken')} className="pxw-resizable-textarea" />
            </StepField>

            <StepField wide label={<span>Purchase Order charges <span className="faded_2">(comma or newline separated)</span><Tooltip title="refer your PO charges page" arrow placement="top" classes={{ popper: 'pxw-tooltip-popper' }}><HelpOutline className="pxw-info-icon" /></Tooltip></span>}>
              <TextField size="small" fullWidth required multiline rows={3} name="purchaseOrderCharges" placeholder="CHARGES_01, CHARGES_02..." value={data.purchaseOrderCharges} onChange={set('purchaseOrderCharges')} className="pxw-resizable-textarea" />
            </StepField>

            <div className="margin-top-1">
              <FormControlLabel
                label={
                  <span className="px-one-line-data">
                    <span>Manually configure sales tax in Quickbooks <span className="pxw-required-star">*</span></span>
                    <Tooltip title="Watch tutorial video" arrow placement="top" classes={{ popper: 'pxw-tooltip-popper' }}>
                      <a href={QBO_TUTORIAL_VIDEO_URL} target="_blank" rel="noopener noreferrer" className="pxw-video-link" onClick={handleVideoClick}>
                        <OndemandVideo className="pxw-video-icon" />
                      </a>
                    </Tooltip>
                  </span>
                }
                control={<PrimaryCheckbox name="manuallyConfigureSalesTax" checked={data.manuallyConfigureSalesTax} onChange={setCheckbox} />}
              />
            </div>
          </>
        )}
      </StepFormContainer>
    </StepCard>
  );
};

export default QuickBooksStep;
